import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeContext, type ThemeContextValue } from './ThemeContext.ts';
import type { ResolvedTheme, ThemeMode } from './theme.type.ts';
import {
  applyTheme,
  getStoredTheme,
  getSystemTheme,
  persistTheme,
  resolveTheme,
} from './theme.util.ts';

/**
 * 전역 테마 Provider.
 *
 * - 기본 동작: 저장된 선택값이 있으면 그것을, 없으면 'light' 를 기본으로 한다.
 *   (사용자가 'system' 을 명시 선택한 경우에만 OS 설정을 따른다.)
 * - 'system' 모드일 때는 OS 테마 변경에 실시간 반응한다(matchMedia 구독).
 * - 초기 페인트 깜빡임(FOUC)은 index.html 의 인라인 스크립트가 먼저 막고,
 *   여기서는 React 상태와 DOM 클래스를 동기화한다.
 *
 * 클라이언트 상태이므로 Context + useState 로 관리(프로젝트 컨벤션).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredTheme() ?? 'light');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredTheme() ?? 'light'),
  );

  // mode 변경 → 해석값 갱신. mode 로부터 계산되는 **파생 상태**라 렌더 중에 맞춘다
  // (useDerivedState 패턴). effect 로 하면 한 프레임 동안 이전 테마로 그려져 깜빡인다.
  const [lastMode, setLastMode] = useState<ThemeMode>(mode);
  if (lastMode !== mode) {
    setLastMode(mode);
    setResolvedTheme(resolveTheme(mode));
  }

  // DOM 클래스 적용은 진짜 외부 시스템 동기화 → 여기가 effect 의 정석 용도다.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // 'system' 모드에서 OS 테마 변경 실시간 반영.
  // setState 가 effect 본문이 아니라 **구독 콜백** 안에서 일어나므로 문제되지 않는다.
  // (DOM 적용은 위 effect 가 resolvedTheme 변경을 받아 처리한다)
  useEffect(() => {
    if (mode !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(getSystemTheme());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    persistTheme(next);
    setModeState(next);
  }, []);

  const toggle = useCallback(() => {
    // 현재 적용된 테마 기준으로 반대값을 명시 선택(=system 해제)
    setResolvedTheme((prev) => {
      const next: ResolvedTheme = prev === 'dark' ? 'light' : 'dark';
      persistTheme(next);
      setModeState(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode, toggle }),
    [mode, resolvedTheme, setMode, toggle],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
